import { cloneDeep, isEqual, isObject, snakeCase } from "lodash";

export const snakecaseObject = (obj: Record<string, any>) => {
  if (!obj) {
    return {};
  }

  return Object.entries(obj).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [snakeCase(key)]: value,
    }),
    {},
  );
};

export const lowercase = (
  value: Record<string, any> | Array<any> | string | number | null,
  levels = 1,
) => {
  if (!value) {
    return value;
  }

  if (typeof value === "string") {
    return value.toLowerCase();
  }

  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.map((v) => lowercase(v, levels));
    }

    return Object.entries(value).reduce(
      (acc, [key, v]) => ({
        ...acc,
        [key.toLowerCase()]: levels > 1 ? lowercase(v, levels - 1) : v,
      }),
      {},
    );
  }

  return value;
};

export const parseIfString = (obj: string | Record<string, unknown>) =>
  typeof obj === "string" ? JSON.parse(obj) : obj;

/**
 * Deeply removes all `undefined` values from object.
 *
 * Arrays:
 * - Cleans objects in arrays but does not remove `undefined` values from arrays
 */
export const removeUndefinedKeysDeep = (obj: unknown) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => removeUndefinedKeysDeep(item));
  }
  const newObj = {} as { [key: string]: any };

  for (const key in obj) {
    // biome-ignore lint/suspicious/noPrototypeBuiltins: TODO: fix
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (value !== undefined) {
        newObj[key] = removeUndefinedKeysDeep(value);
      }
    }
  }

  return newObj;
};

/**
 * Recursively removes a specific key from an object at all depths
 * @param obj - The object to process
 * @param keyToIgnore - The key name to remove
 * @returns A new object without the specified key at any depth
 */
function removeKeyDeep(obj: any, keyToIgnore: string): any {
  if (!isObject(obj)) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => removeKeyDeep(item, keyToIgnore));
  }

  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key !== keyToIgnore) {
      result[key] = removeKeyDeep(value, keyToIgnore);
    }
  }
  return result;
}

/**
 * Normalizes an object by treating all nullish fields as undefined
 * @param obj - The object to normalize
 * @returns A new object with normalized values
 */
function normalizeNullishDeep(obj: any): any {
  if (!isObject(obj)) {
    return obj == null ? undefined : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => normalizeNullishDeep(item));
  }

  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const normalizedValue = normalizeNullishDeep(value);
    if (normalizedValue != null) {
      result[key] = normalizedValue;
    }
  }
  return result;
}

/**
 * Compares two objects deeply while ignoring a specific key at any depth
 * and treating missing, null, or undefined fields as equivalent
 * @param obj1 - First object to compare
 * @param obj2 - Second object to compare
 * @param keyToIgnore - The key name to ignore during comparison
 * @returns True if objects are equal (ignoring the specified key), false otherwise
 */
export function isEqualIgnoringNullishAndKey(
  obj1: any,
  obj2: any,
  keyToIgnore: string,
): boolean {
  // Create deep copies to avoid modifying original objects
  const cleanObj1 = removeKeyDeep(
    normalizeNullishDeep(cloneDeep(obj1)),
    keyToIgnore,
  );
  const cleanObj2 = removeKeyDeep(
    normalizeNullishDeep(cloneDeep(obj2)),
    keyToIgnore,
  );

  // Use lodash isEqual for the deep comparison
  return isEqual(cleanObj1, cleanObj2);
}
